import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/Layout";
import { EmptyState } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import api, { formatApiError } from "@/lib/api";
import {
  PRIORITIES,
  AVISO_STATUS,
} from "@/lib/constants";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import {
  Plus,
  Trash2,
  Search,
  SlidersHorizontal,
  Bell,
  AlertTriangle,
  UserCheck,
  X,
  Loader2,
} from "lucide-react";

import { toast } from "sonner";

const EMPTY_FORM = {
  title: "",
  description: "",
  client_id: "",
  priority: "media",
  status: "nuevo",
  assigned_technician_id: "",
};

const ALL_FILTER = "todos";

function SummaryCard({
  icon: Icon,
  label,
  value,
  color,
}) {
  return (
    <div className="navirox-card flex items-center gap-4 p-5">
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl"
        style={{
          backgroundColor: `${color}15`,
        }}
      >
        <Icon
          className="h-5 w-5"
          style={{ color }}
          strokeWidth={1.8}
        />
      </div>

      <div>
        <p className="font-display text-2xl font-bold text-[#1D1D1F]">
          {value}
        </p>

        <p className="text-sm text-[#86868B]">
          {label}
        </p>
      </div>
    </div>
  );
}

export default function Avisos() {
  const [avisos, setAvisos] = useState([]);
  const [clients, setClients] = useState([]);
  const [techs, setTechs] = useState([]);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] =
    useState(ALL_FILTER);
  const [priorityFilter, setPriorityFilter] =
    useState(ALL_FILTER);

  const loadAvisos = async () => {
    try {
      const response = await api.get("/avisos");
      setAvisos(response.data || []);
    } catch (error) {
      toast.error(
        formatApiError(error) ||
          "No se pudieron cargar los avisos."
      );
    }
  };

  const loadInitialData = async () => {
    setLoading(true);

    try {
      const [
        avisosResponse,
        clientsResponse,
        techsResponse,
      ] = await Promise.all([
        api.get("/avisos"),
        api.get("/clients"),
        api.get("/technicians"),
      ]);

      setAvisos(avisosResponse.data || []);
      setClients(clientsResponse.data || []);
      setTechs(techsResponse.data || []);
    } catch (error) {
      toast.error(
        formatApiError(error) ||
          "No se pudieron cargar los datos."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const save = async (event) => {
    event.preventDefault();

    if (!form.title.trim()) {
      toast.error("Escribe el título del aviso.");
      return;
    }

    if (!form.client_id) {
      toast.error("Selecciona un cliente.");
      return;
    }

    setSaving(true);

    try {
      const hasTechnician =
        Boolean(form.assigned_technician_id);

      const payload = {
        ...form,
        title: form.title.trim(),
        description: form.description.trim(),
        assigned_technician_id:
          form.assigned_technician_id || null,
        status: hasTechnician
          ? "asignado"
          : form.status,
      };

      await api.post("/avisos", payload);

      toast.success("Aviso creado correctamente.");

      setOpen(false);
      setForm(EMPTY_FORM);

      await loadAvisos();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (id, status) => {
    setUpdatingId(id);

    try {
      await api.put(`/avisos/${id}`, {
        status,
      });

      setAvisos((current) =>
        current.map((aviso) =>
          aviso.id === id
            ? {
                ...aviso,
                status,
              }
            : aviso
        )
      );

      toast.success("Estado actualizado.");
    } catch (error) {
      toast.error(formatApiError(error));
      await loadAvisos();
    } finally {
      setUpdatingId(null);
    }
  };

  const assign = async (
    id,
    technicianId
  ) => {
    setUpdatingId(id);

    try {
      const status = technicianId
        ? "asignado"
        : "nuevo";

      await api.put(`/avisos/${id}`, {
        assigned_technician_id:
          technicianId || null,
        status,
      });

      setAvisos((current) =>
        current.map((aviso) =>
          aviso.id === id
            ? {
                ...aviso,
                assigned_technician_id:
                  technicianId || null,
                status,
              }
            : aviso
        )
      );

      toast.success(
        technicianId
          ? "Técnico asignado."
          : "Asignación retirada."
      );
    } catch (error) {
      toast.error(formatApiError(error));
      await loadAvisos();
    } finally {
      setUpdatingId(null);
    }
  };

  const remove = async (aviso) => {
    const confirmed = window.confirm(
      `¿Seguro que quieres eliminar el aviso “${aviso.title}”?`
    );

    if (!confirmed) return;

    setDeletingId(aviso.id);

    try {
      await api.delete(
        `/avisos/${aviso.id}`
      );

      setAvisos((current) =>
        current.filter(
          (item) => item.id !== aviso.id
        )
      );

      toast.success("Aviso eliminado.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeletingId(null);
    }
  };

  const filteredAvisos = useMemo(() => {
    const normalizedQuery = query
      .trim()
      .toLowerCase();

    return avisos.filter((aviso) => {
      const matchesQuery =
        !normalizedQuery ||
        [
          aviso.title,
          aviso.description,
          aviso.client_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery);

      const matchesStatus =
        statusFilter === ALL_FILTER ||
        aviso.status === statusFilter;

      const matchesPriority =
        priorityFilter === ALL_FILTER ||
        aviso.priority === priorityFilter;

      return (
        matchesQuery &&
        matchesStatus &&
        matchesPriority
      );
    });
  }, [
    avisos,
    query,
    statusFilter,
    priorityFilter,
  ]);

  const openAvisos = avisos.filter(
    (aviso) =>
      aviso.status !== "resuelto" &&
      aviso.status !== "cancelado"
  ).length;

  const urgentAvisos = avisos.filter(
    (aviso) =>
      aviso.priority === "urgente" &&
      aviso.status !== "resuelto" &&
      aviso.status !== "cancelado"
  ).length;

  const assignedAvisos = avisos.filter(
    (aviso) =>
      Boolean(
        aviso.assigned_technician_id
      )
  ).length;

  const filtersActive =
    Boolean(query.trim()) ||
    statusFilter !== ALL_FILTER ||
    priorityFilter !== ALL_FILTER;

  const clearFilters = () => {
    setQuery("");
    setStatusFilter(ALL_FILTER);
    setPriorityFilter(ALL_FILTER);
  };

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <PageHeader
        title="Avisos"
        subtitle={`${avisos.length} avisos · ${openAvisos} abiertos`}
        action={
          <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
              setOpen(nextOpen);

              if (!nextOpen) {
                setForm(EMPTY_FORM);
              }
            }}
          >
            <DialogTrigger asChild>
              <button
                data-testid="new-aviso-btn"
                type="button"
                className="flex items-center gap-2 rounded-full bg-[#0066CC] px-5 py-2.5 font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.3)] transition-all hover:-translate-y-0.5 hover:bg-[#004499]"
              >
                <Plus className="h-4 w-4" />
                Nuevo aviso
              </button>
            </DialogTrigger>

            <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-3xl border-none bg-white p-6 shadow-2xl">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-bold text-[#1D1D1F]">
                  Nuevo aviso
                </DialogTitle>

                <p className="mt-1 text-sm text-[#86868B]">
                  Registra una incidencia y asígnala
                  a un técnico.
                </p>
              </DialogHeader>

              <form
                onSubmit={save}
                className="mt-4 space-y-4"
              >
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                    Título
                  </label>

                  <input
                    data-testid="aviso-title"
                    value={form.title}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        title:
                          event.target.value,
                      })
                    }
                    required
                    autoFocus
                    className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 text-[#1D1D1F] outline-none transition-all focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                    placeholder="Ej. Fuga de agua en cocina"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                    Cliente
                  </label>

                  <select
                    data-testid="aviso-client"
                    value={form.client_id}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        client_id:
                          event.target.value,
                      })
                    }
                    required
                    className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 text-[#1D1D1F] outline-none transition-all focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                  >
                    <option value="">
                      Selecciona un cliente
                    </option>

                    {clients.map((client) => (
                      <option
                        key={client.id}
                        value={client.id}
                      >
                        {client.name}
                      </option>
                    ))}
                  </select>

                  {clients.length === 0 && (
                    <p className="mt-2 text-xs text-[#FF9500]">
                      Antes debes crear al menos un
                      cliente.
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                    Descripción
                  </label>

                  <textarea
                    data-testid="aviso-description"
                    value={form.description}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        description:
                          event.target.value,
                      })
                    }
                    rows={4}
                    className="w-full resize-none rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 text-[#1D1D1F] outline-none transition-all focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                    placeholder="Describe el problema, ubicación y cualquier detalle importante."
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                      Prioridad
                    </label>

                    <select
                      data-testid="aviso-priority"
                      value={form.priority}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          priority:
                            event.target.value,
                        })
                      }
                      className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 text-[#1D1D1F] outline-none transition-all focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                    >
                      {PRIORITIES.map(
                        (priority) => (
                          <option
                            key={
                              priority.value
                            }
                            value={
                              priority.value
                            }
                          >
                            {priority.label}
                          </option>
                        )
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                      Técnico
                    </label>

                    <select
                      value={
                        form.assigned_technician_id
                      }
                      onChange={(event) =>
                        setForm({
                          ...form,
                          assigned_technician_id:
                            event.target.value,
                        })
                      }
                      className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 text-[#1D1D1F] outline-none transition-all focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                    >
                      <option value="">
                        Asignar más tarde
                      </option>

                      {techs.map(
                        (technician) => (
                          <option
                            key={
                              technician.id
                            }
                            value={
                              technician.id
                            }
                          >
                            {technician.name}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() =>
                      setOpen(false)
                    }
                    className="flex-1 rounded-full border border-[#D2D2D7] bg-white py-3 font-medium text-[#1D1D1F] transition-colors hover:bg-[#F5F5F7]"
                  >
                    Cancelar
                  </button>

                  <button
                    data-testid="save-aviso"
                    type="submit"
                    disabled={
                      saving ||
                      clients.length === 0
                    }
                    className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#0066CC] py-3 font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.25)] transition-all hover:bg-[#004499] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}

                    {saving
                      ? "Guardando..."
                      : "Crear aviso"}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryCard
          icon={Bell}
          label="Avisos abiertos"
          value={openAvisos}
          color="#0066CC"
        />

        <SummaryCard
          icon={AlertTriangle}
          label="Avisos urgentes"
          value={urgentAvisos}
          color="#FF3B30"
        />

        <SummaryCard
          icon={UserCheck}
          label="Avisos asignados"
          value={assignedAvisos}
          color="#34C759"
        />
      </section>

      <section className="navirox-card mb-6 p-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#86868B]" />

            <input
              value={query}
              onChange={(event) =>
                setQuery(event.target.value)
              }
              className="w-full rounded-xl bg-[#F5F5F7] py-3 pl-11 pr-10 text-sm text-[#1D1D1F] outline-none transition-all focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
              placeholder="Buscar por título, cliente o descripción..."
            />

            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-[#86868B] hover:bg-white hover:text-[#1D1D1F]"
                aria-label="Borrar búsqueda"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative">
              <SlidersHorizontal className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#86868B]" />

              <select
                value={statusFilter}
                onChange={(event) =>
                  setStatusFilter(
                    event.target.value
                  )
                }
                className="w-full min-w-44 appearance-none rounded-xl bg-[#F5F5F7] py-3 pl-10 pr-8 text-sm text-[#1D1D1F] outline-none focus:ring-2 focus:ring-[#0066CC]/20 sm:w-auto"
              >
                <option value={ALL_FILTER}>
                  Todos los estados
                </option>

                {AVISO_STATUS.map(
                  (status) => (
                    <option
                      key={status.value}
                      value={status.value}
                    >
                      {status.label}
                    </option>
                  )
                )}
              </select>
            </div>

            <select
              value={priorityFilter}
              onChange={(event) =>
                setPriorityFilter(
                  event.target.value
                )
              }
              className="w-full min-w-44 rounded-xl bg-[#F5F5F7] px-4 py-3 text-sm text-[#1D1D1F] outline-none focus:ring-2 focus:ring-[#0066CC]/20 sm:w-auto"
            >
              <option value={ALL_FILTER}>
                Todas las prioridades
              </option>

              {PRIORITIES.map(
                (priority) => (
                  <option
                    key={priority.value}
                    value={priority.value}
                  >
                    {priority.label}
                  </option>
                )
              )}
            </select>

            {filtersActive && (
              <button
                type="button"
                onClick={clearFilters}
                className="rounded-xl px-4 py-3 text-sm font-medium text-[#0066CC] transition-colors hover:bg-[#0066CC]/10"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        </div>

        <p className="mt-3 text-xs text-[#86868B]">
          Mostrando {filteredAvisos.length} de{" "}
          {avisos.length} avisos
        </p>
      </section>

      {loading ? (
        <div className="flex min-h-72 items-center justify-center">
          <div className="text-center">
            <Loader2 className="mx-auto h-7 w-7 animate-spin text-[#0066CC]" />

            <p className="mt-3 text-sm text-[#86868B]">
              Cargando avisos...
            </p>
          </div>
        </div>
      ) : avisos.length === 0 ? (
        <div className="navirox-card py-8">
          <EmptyState
            title="No hay avisos"
            subtitle="Crea tu primer aviso para asignarlo a un técnico y convertirlo en una orden de trabajo."
          />

          <div className="-mt-4 flex justify-center pb-6">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="flex items-center gap-2 rounded-full bg-[#0066CC] px-5 py-2.5 text-sm font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.25)] hover:bg-[#004499]"
            >
              <Plus className="h-4 w-4" />
              Crear primer aviso
            </button>
          </div>
        </div>
      ) : filteredAvisos.length === 0 ? (
        <div className="navirox-card flex min-h-72 flex-col items-center justify-center p-8 text-center">
          <Search className="h-9 w-9 text-[#B0B0B5]" />

          <h3 className="mt-4 font-display text-lg font-semibold text-[#1D1D1F]">
            No hay resultados
          </h3>

          <p className="mt-1 text-sm text-[#86868B]">
            Prueba a cambiar la búsqueda o los
            filtros.
          </p>

          <button
            type="button"
            onClick={clearFilters}
            className="mt-5 rounded-full bg-[#0066CC]/10 px-5 py-2.5 text-sm font-medium text-[#0066CC] hover:bg-[#0066CC]/15"
          >
            Limpiar filtros
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredAvisos.map((aviso) => {
            const priority =
              PRIORITIES.find(
                (item) =>
                  item.value === aviso.priority
              );

            const isUpdating =
              updatingId === aviso.id;

            const isDeleting =
              deletingId === aviso.id;

            return (
              <article
                key={aviso.id}
                data-testid={`aviso-row-${aviso.id}`}
                className="navirox-card group relative overflow-hidden p-5 transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <div
                  className="absolute inset-y-0 left-0 w-1.5"
                  style={{
                    backgroundColor:
                      priority?.color ||
                      "#86868B",
                  }}
                />

                <div className="flex flex-col gap-5 pl-2 xl:flex-row xl:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-display text-base font-semibold text-[#1D1D1F]">
                        {aviso.title}
                      </h3>

                      <StatusBadge
                        list={PRIORITIES}
                        value={aviso.priority}
                      />

                      <StatusBadge
                        list={AVISO_STATUS}
                        value={aviso.status}
                      />
                    </div>

                    <p className="mt-2 text-sm font-medium text-[#0066CC]">
                      {aviso.client_name ||
                        "Cliente sin nombre"}
                    </p>

                    {aviso.description && (
                      <p className="mt-2 line-clamp-2 whitespace-pre-wrap text-sm leading-relaxed text-[#86868B]">
                        {aviso.description}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <select
                      data-testid={`aviso-assign-${aviso.id}`}
                      value={
                        aviso.assigned_technician_id ||
                        ""
                      }
                      disabled={
                        isUpdating ||
                        isDeleting
                      }
                      onChange={(event) =>
                        assign(
                          aviso.id,
                          event.target.value
                        )
                      }
                      className="min-w-44 rounded-xl bg-[#F5F5F7] px-3 py-2 text-sm text-[#1D1D1F] outline-none transition-all focus:ring-2 focus:ring-[#0066CC]/20 disabled:opacity-60"
                    >
                      <option value="">
                        Sin asignar
                      </option>

                      {techs.map(
                        (technician) => (
                          <option
                            key={
                              technician.id
                            }
                            value={
                              technician.id
                            }
                          >
                            {technician.name}
                          </option>
                        )
                      )}
                    </select>

                    <select
                      data-testid={`aviso-status-${aviso.id}`}
                      value={aviso.status}
                      disabled={
                        isUpdating ||
                        isDeleting
                      }
                      onChange={(event) =>
                        changeStatus(
                          aviso.id,
                          event.target.value
                        )
                      }
                      className="min-w-36 rounded-xl bg-[#F5F5F7] px-3 py-2 text-sm text-[#1D1D1F] outline-none transition-all focus:ring-2 focus:ring-[#0066CC]/20 disabled:opacity-60"
                    >
                      {AVISO_STATUS.map(
                        (status) => (
                          <option
                            key={
                              status.value
                            }
                            value={
                              status.value
                            }
                          >
                            {status.label}
                          </option>
                        )
                      )}
                    </select>

                    <button
                      data-testid={`aviso-delete-${aviso.id}`}
                      type="button"
                      disabled={
                        isUpdating ||
                        isDeleting
                      }
                      onClick={() =>
                        remove(aviso)
                      }
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[#B0B0B5] transition-all hover:bg-[#FFEBEA] hover:text-[#FF3B30] disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label="Eliminar aviso"
                    >
                      {isDeleting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}